// src/hooks/useOptimizationStudio.ts
import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchOptimizationStudioData,
  type OptimizationStudioData,
} from "../../services/fix-actions/optimization-studio-service";

interface UseOptimizationStudioArgs {
  brandId: string;
}

export function useOptimizationStudio({ brandId }: UseOptimizationStudioArgs) {
  const [data, setData] = useState<OptimizationStudioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchOptimizationStudioData({ brandId });
      if (!mountedRef.current) return;
      setData(result);
    } catch (e: any) {
      if (!mountedRef.current) return;
      setError(e instanceof Error ? e : new Error("Failed to load studio data"));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    mountedRef.current = true;
    fetch();
    return () => {
      mountedRef.current = false;
    };
  }, [fetch]);

  // Listen for refresh events dispatched by fix-actions-page after generate/approve
  useEffect(() => {
    const handler = () => fetch();
    window.addEventListener("optimization_studio_refresh", handler);
    return () => window.removeEventListener("optimization_studio_refresh", handler);
  }, [fetch]);

  return { data, loading, error, refetch: fetch };
}